import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { createContext, useContext } from "react";

import { useCurrentOrganization } from "@/core/hooks/use-current-organization";
import type {
  AuditNode,
  AuditProject,
  CorporateFinanceProject,
  FasProject,
} from "@/core/lib/access";
import {
  auditBookingYear,
  isAuditClientProject,
  isAuditProject,
} from "@/core/lib/access";
import type { ProjectModule } from "@/core/lib/project-modules";
import { lastOpenedDossierId } from "@/features/project/lib/last-opened-dossier";
import type { Project } from "@/openapi";
import { getProjectHierarchyOptions } from "@/openapi/@tanstack/react-query.gen";

// how a module's tree is navigated: a holding switches between itself and its
// direct entities; a client tree resolves the active dossier beneath clients
type HierarchyShape = "holding" | "client-tree";

const MODULE_SHAPES = {
  audit: "client-tree",
  "corporate-finance": "holding",
  fas: "holding",
} as const satisfies Record<ProjectModule, HierarchyShape>;

export type HoldingModule = {
  [Module in ProjectModule]: (typeof MODULE_SHAPES)[Module] extends "holding"
    ? Module
    : never;
}[ProjectModule];

const isHoldingModule = (module: ProjectModule): module is HoldingModule =>
  MODULE_SHAPES[module] === "holding";

export interface ProjectPageContextValue {
  module: ProjectModule;
  /** root project id from the route param */
  projectId: string;
}

export const ProjectPageContext = createContext<ProjectPageContextValue | null>(
  null,
);

function useProjectRoute() {
  const context = useContext(ProjectPageContext);

  if (context === null) {
    throw new Error("Project page context not found");
  }

  return context;
}

function useHierarchy() {
  const { projectId } = useProjectRoute();
  const { organizationId } = useCurrentOrganization({ throw: true });
  const { data } = useSuspenseQuery(
    getProjectHierarchyOptions({
      path: { org_id: organizationId, project_id: projectId },
    }),
  );

  return data;
}

/** storage key for the client tree's last-opened dossier; flat legacy roots
 * (the root IS a dossier, old backend) share the list page's company key */
export function clientRootKey(root: Project): string {
  if (isAuditProject(root)) {
    const companyId = root.parameters.company_id;
    return companyId ? `company:${companyId}` : root.id;
  }
  return root.id;
}

function defaultDossierOf(
  clientId: string,
  dossiers: AuditProject[],
): AuditProject | undefined {
  const clientDossiers = dossiers
    .filter((dossier) => dossier.parent_project_id === clientId)
    .toSorted((a, b) => auditBookingYear(b) - auditBookingYear(a));
  if (clientDossiers.length === 0) {
    return undefined;
  }
  const remembered = lastOpenedDossierId(clientId);
  return (
    clientDossiers.find((dossier) => dossier.id === remembered) ??
    clientDossiers[0]
  );
}

/** Resolve the active audit dossier of a client tree. Pure; also used by
 * route beforeLoads against the cached hierarchy. */
export function resolveAuditActive(
  root: Project,
  children: Project[],
  entityProjectId: string | undefined,
): AuditNode {
  const dossiers = children.filter(isAuditProject);
  if (entityProjectId) {
    const selected = dossiers.find((dossier) => dossier.id === entityProjectId);
    if (selected) {
      return selected;
    }
    // a subsidiary CLIENT id (e.g. from a canonicalized deep link) selects
    // that client's remembered/newest dossier
    const selectedClient = children.find(
      (child) => child.id === entityProjectId && isAuditClientProject(child),
    );
    if (selectedClient) {
      const clientDefault = defaultDossierOf(selectedClient.id, dossiers);
      if (clientDefault) {
        return clientDefault;
      }
    }
  }
  if (dossiers.length === 0) {
    // flat legacy dossier (the root IS the dossier) or a dossier-less client;
    // an audit tree holds nothing but these two node kinds
    return root as AuditNode;
  }
  // default within the root client's own dossiers; a pure holding without
  // own dossiers falls back to the newest dossier in the subtree
  const rootDefault =
    defaultDossierOf(root.id, dossiers) ??
    dossiers.find(
      (dossier) => dossier.id === lastOpenedDossierId(clientRootKey(root)),
    );
  return (
    rootDefault ??
    dossiers.toSorted((a, b) => auditBookingYear(b) - auditBookingYear(a))[0]
  );
}

export type ProjectHierarchy =
  | {
      module: "audit";
      shape: "client-tree";
      root: AuditNode;
      children: AuditNode[];
      active: AuditNode;
      activeClient: AuditNode;
    }
  | {
      module: "corporate-finance";
      shape: "holding";
      root: CorporateFinanceProject;
      children: CorporateFinanceProject[];
      active: CorporateFinanceProject;
      activeClient: CorporateFinanceProject;
    }
  | {
      module: "fas";
      shape: "holding";
      root: FasProject;
      children: FasProject[];
      active: FasProject;
      activeClient: FasProject;
    };

export function useProjectHierarchy(): ProjectHierarchy {
  const { module } = useProjectRoute();
  const hierarchy = useHierarchy();
  const { entityProjectId } = useSearch({ strict: false });

  // the route module decides which node kinds the tree can hold; the backend
  // refuses to mix them under one root
  if (isHoldingModule(module)) {
    const root = hierarchy.root as HoldingHierarchy["root"];
    const children = hierarchy.children as HoldingHierarchy["children"];
    // a holding switches entities directly
    const active =
      children.find((child) => child.id === entityProjectId) ?? root;
    return {
      module,
      shape: "holding",
      root,
      children,
      active,
      activeClient: root,
    } as HoldingHierarchy;
  }

  const root = hierarchy.root as AuditNode;
  const children = hierarchy.children as AuditNode[];
  // audit resolves the active DOSSIER (year) of the client tree, never the
  // client node itself unless the tree has no dossiers at all
  const active = resolveAuditActive(
    hierarchy.root,
    hierarchy.children,
    entityProjectId,
  );

  return {
    module,
    shape: "client-tree",
    root,
    children,
    active,
    // the client node the active dossier belongs to: its parent in a holding
    // tree, else the root itself
    activeClient:
      children.find((child) => child.id === active.parent_project_id) ?? root,
  };
}

/** Assert the resolved active node is a dossier. Route `beforeLoad`s beneath a
 *  feature guard have this guarantee but cannot express it in the type. */
export function requireAuditDossier(active: AuditNode): AuditProject {
  if (!isAuditProject(active)) {
    throw new Error("expected an active audit dossier");
  }
  return active;
}

/** The audit client tree. Only valid beneath an audit route. */
export function useAuditHierarchy() {
  const hierarchy = useProjectHierarchy();
  if (hierarchy.module !== "audit") {
    throw new Error("useAuditHierarchy requires an audit route");
  }
  return hierarchy;
}

type HoldingHierarchy = Extract<ProjectHierarchy, { shape: "holding" }>;

/** A holding tree of any module. Only valid beneath a holding route. */
export function useHoldingHierarchy(): HoldingHierarchy {
  const hierarchy = useProjectHierarchy();
  if (hierarchy.shape !== "holding") {
    throw new Error("useHoldingHierarchy requires a holding route");
  }
  return hierarchy;
}

/** The corporate finance tree. Only valid beneath a corporate finance route. */
export function useCorporateFinanceHierarchy() {
  const hierarchy = useProjectHierarchy();
  if (hierarchy.module !== "corporate-finance") {
    throw new Error(
      "useCorporateFinanceHierarchy requires a corporate finance route",
    );
  }
  return hierarchy;
}

/**
 * The active audit dossier. Only valid beneath a feature-gated audit route,
 * where a dossier-less client node can never be active: every such guard reads
 * `features`, and an `audit_client` has none.
 */
export function useCurrentAuditProject(): AuditProject {
  const { active } = useProjectHierarchy();
  if (!isAuditProject(active)) {
    throw new Error("useCurrentAuditProject requires an active audit dossier");
  }
  return active;
}

/**
 * The project all data fetching should target: the active entity in
 * corporate finance, the active dossier in audit.
 */
export function useCurrentProject(): Project {
  return useProjectHierarchy().active;
}

export function useEntitySwitcher() {
  const { module } = useProjectRoute();
  const hierarchy = useHierarchy();
  const { entityProjectId } = useSearch({ strict: false });
  const navigate = useNavigate();
  const nodes = [hierarchy.root, ...hierarchy.children];

  const shared = {
    activeEntityId: entityProjectId ?? null,
    setActiveEntityId: (projectId: string) => {
      navigate({
        to: ".",
        search: (current) => ({
          ...current,
          entityProjectId:
            projectId === hierarchy.root.id ? undefined : projectId,
        }),
      });
    },
  };

  return isHoldingModule(module)
    ? {
        module,
        shape: "holding" as const,
        entities: nodes as HoldingHierarchy["root"][],
        ...shared,
      }
    : {
        module,
        shape: "client-tree" as const,
        entities: nodes as AuditNode[],
        ...shared,
      };
}

/** The switchable entities of a holding tree. Only valid beneath a holding
 *  route. */
export function useHoldingEntities(): HoldingHierarchy["root"][] {
  const switcher = useEntitySwitcher();
  if (switcher.shape !== "holding") {
    throw new Error("useHoldingEntities requires a holding route");
  }
  return switcher.entities;
}

/** The switchable corporate finance entities. Only valid beneath a corporate
 *  finance route. */
export function useCorporateFinanceEntities(): CorporateFinanceProject[] {
  const switcher = useEntitySwitcher();
  if (switcher.module !== "corporate-finance") {
    throw new Error(
      "useCorporateFinanceEntities requires a corporate finance route",
    );
  }
  return switcher.entities as CorporateFinanceProject[];
}
